import { deepmerge } from 'deepmerge-ts';
import { cloneElement, Fragment, useState } from 'react';

import { Box } from '../box';
import { IconButton, type IconButtonProps } from '../button';
import { Flex, type FlexProps } from '../flex';
import { MoreVertIcon } from '../icons';
import { Menu, MenuItem, type MenuProps, type MenuItemProps } from '../menu';
import { Text } from '../text';
import { Tooltip, type TooltipProps } from '../tooltip';
import { mergeSx } from '../utils';

export interface MoreMenuIconButtonOption extends Pick<MenuItemProps, 'sx' | 'onClick'> {
  icon: React.ReactElement;
  label: string;
  disabled?: boolean;
  tooltip?: TooltipProps['title'];
  'data-testid'?: string;
}

export interface MoreMenuIconButtonProps {
  trigger?: {
    id: string;
    icon: React.ReactElement;
  };
  options: MoreMenuIconButtonOption[];
  slotProps?: {
    iconButton?: IconButtonProps;
    menu?: Omit<MenuProps, 'open' | 'options'>;
    menuItemWrapper?: FlexProps;
    menuItem?: MenuItemProps;
    tooltip?: Omit<TooltipProps, 'title' | 'children'>;
  };
}

const defaultIconStyle = {
  mr: 2,
  width: 24,
  height: 24,
};

export default function MoreMenuIconButton(props: Readonly<MoreMenuIconButtonProps>) {
  const { trigger, options, slotProps = {} } = props;

  const {
    iconButton: iconButtonProps,
    menu: menuProps,
    menuItemWrapper: menuItemWrapperProps,
    menuItem: menuItemProps,
    tooltip: tooltipProps,
  } = slotProps;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = anchorEl !== null;

  const { id: buttonId = 'long-btn', icon = <MoreVertIcon /> } = trigger ?? {};

  const menuId = buttonId + '-' + 'menu';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const mergedMenuProps = deepmerge(
    {
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: 'right' },
    } as const,
    menuProps,
  );

  return (
    <Box>
      <IconButton
        {...iconButtonProps}
        aria-label='more'
        id={buttonId}
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleClick}
      >
        {icon}
      </IconButton>
      <Menu
        {...mergedMenuProps}
        sx={mergeSx(menuProps?.sx, {
          '.MuiMenu-list': {
            minWidth: '200px',
          },
        })}
        id={menuId}
        MenuListProps={{
          'aria-labelledby': buttonId,
        }}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCloseMenu}
      >
        <Flex
          {...menuItemWrapperProps}
          sx={{
            flexDirection: 'column',
            gap: (theme) => theme.spacing(1),
            ...menuItemWrapperProps?.sx,
          }}
        >
          {options.map((option) => {
            const {
              icon: menuIcon,
              label,
              onClick,
              sx,
              disabled,
              tooltip,
              'data-testid': dataTestId,
            } = option;
            const mergedMenuIconSx = mergeSx(defaultIconStyle, menuIcon.props.sx);

            const menuItem = (
              <MenuItem
                {...menuItemProps}
                disabled={disabled}
                onClick={(event) => {
                  onClick?.(event);
                  handleCloseMenu(event);
                }}
                sx={sx}
                data-testid={dataTestId}
              >
                {cloneElement(menuIcon, {
                  sx: mergedMenuIconSx,
                })}
                <Text>{label}</Text>
              </MenuItem>
            );

            if (!tooltip) {
              return <Fragment key={`more-menu-${label}`}>{menuItem}</Fragment>;
            }

            const {
              sx: tooltipSx,
              slotProps: tooltipSlotProps,
              ...restTooltipProps
            } = tooltipProps ?? {};

            const existingTooltipSlot = tooltipSlotProps?.tooltip;
            const tooltipSlot =
              typeof existingTooltipSlot === 'function'
                ? existingTooltipSlot
                : {
                    ...existingTooltipSlot,
                    sx: mergeSx(existingTooltipSlot?.sx, tooltipSx),
                  };

            // NOTE: MUI Tooltip does not fire on disabled elements, so wrap in a span
            // https://mui.com/material-ui/react-tooltip/#disabled-elements
            return (
              <Tooltip
                key={`more-menu-${label}`}
                arrow
                placement='left'
                {...restTooltipProps}
                slotProps={{
                  ...tooltipSlotProps,
                  tooltip: tooltipSlot,
                }}
                title={tooltip}
              >
                <Box component='span'>{menuItem}</Box>
              </Tooltip>
            );
          })}
        </Flex>
      </Menu>
    </Box>
  );
}
